import {Component, inject, model, OnInit, Renderer2, signal, ViewChild} from '@angular/core';
import {
  DateComponent, FormComponent, InputComponent,
  InputOptions,
  ModalComponent,
  ModalOptions,
  TableComponent,
  TableOptions
} from "@shared/components";
import {Allergy} from '@core/classes/tables/allergy';
import {APIService} from '@core/services';
import {PatientService} from '@core/services/patient.service';
import {HttpHeaders} from '@angular/common/http';
import {Demographics} from '@core/classes/tables/demographics';
import {environment} from '@env/environment';
import {FILTER_ONLY_NUMBERS, PROJECTS} from '@core/constants';
import {DiagnosticTest} from '@core/classes/tables/diagnostic-test';
import {HTTPMethods, InputModes, InputTypes, ModalTypes} from '@core/enums';
import {
  AbstractControl, FormControl,
  FormGroup, FormGroupDirective,
  FormsModule, NgForm,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import moment from 'moment/moment';
import {checkDate} from '@core/helpers';
import {DropdownComponent} from '@shared/components/dropdown/dropdown.component';
import {ResponseSingle} from '@core/classes/other/response';
import {SizeUnit} from '@core/classes/other/size-unit';

@Component({
  selector: 'ncp-demographics',
  imports: [
    TableComponent,
    ModalComponent,
    FormsModule,
    ReactiveFormsModule,
    InputComponent
  ],
  templateUrl: './demographics.component.html',
  styleUrl: './demographics.component.scss',
})
export class DemographicsComponent implements OnInit {
  @ViewChild('demographics') public demographicsEl!: TableComponent<Demographics>;
  @ViewChild('mod') public mod!: ModalComponent;
  @ViewChild('eFrm') protected emailFormEl?: NgForm;
  @ViewChild('pFrm') protected phoneFormEl?: NgForm;
  @ViewChild('aFrm') protected addressFormEl?: NgForm;

  @ViewChild('newEmail') protected newEmail?: InputComponent;
  @ViewChild('newPhone') protected newPhone?: InputComponent;

  protected readonly apiService = inject(APIService);
  protected readonly patientService = inject(PatientService);
  protected renderer2 = inject(Renderer2);

  protected emailForm: FormGroup = new FormGroup({
    old_value: new FormControl({ value: this.patientService.data?.email, disabled: true }, { nonNullable: true, validators: Validators.required }),
    new_value: new FormControl<string | undefined>('', [Validators.required, Validators.email]),
    otp_code: new FormControl<string | undefined>({value:'',disabled:true}),
  }, {
    validators: [this.formValidator('email')]
  });

  protected phoneForm: FormGroup = new FormGroup({
    old_value: new FormControl({ value: this.patientService.data?.phone, disabled: true }, { nonNullable: true, validators: Validators.required }),
    new_value: new FormControl<string | undefined>('', [Validators.required]),
    otp_code: new FormControl<string | undefined>({value:'',disabled:true}),
  }, {
    validators: [this.formValidator('phone')]
  });

  protected addressForm: FormGroup = new FormGroup({
    address: new FormControl(this.patientService.data?.address?.address, [Validators.required]),
    addressNumber: new FormControl(this.patientService.data?.address?.addressNumber, [Validators.required]),
    postalCode: new FormControl(this.patientService.data?.address?.postalCode, [Validators.required]),
    city: new FormControl(this.patientService.data?.address?.city, [Validators.required]),
    country: new FormControl({value: this.patientService.data?.address?.country, disabled:true}, []),
  }, {
    validators: [this.formValidator('address')]
  })

  get oldEmailAddress() {
    return this.emailForm.controls['old_value'] as FormControl;
  }

  get newEmailAddress() {
    return this.emailForm.controls['new_value'] as FormControl;
  }

  get oldPhoneNumber() {
    return this.phoneForm.controls['old_value'] as FormControl;
  }

  get newPhoneNumber() {
    return this.phoneForm.controls['new_value'] as FormControl;
  }

  get emailOTPCode() {
    return this.emailForm.controls['otp_code'] as FormControl;
  }

  get phoneOTPCode() {
    return this.phoneForm.controls['otp_code'] as FormControl;
  }

  get address() {
    return this.addressForm.get('address') as FormControl;
  }

  get number() {
    return this.addressForm.get('addressNumber') as FormControl;
  }

  get postCode() {
    return this.addressForm.get('postalCode') as FormControl;
  }

  get city() {
    return this.addressForm.get('city') as FormControl;
  }

  get country() {
    return this.addressForm.get('country') as FormControl;
  }

  formValidator(type: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let errors: ValidationErrors | null = {};

      switch (type) {
        case 'phone': {
          let oldValue = (control as FormGroup).controls['old_value'];
          let newValue = (control as FormGroup).controls['new_value'];
          let otpCode = (control as FormGroup).controls['otp_code'];

          if(newValue.value === undefined || newValue.value === null || newValue.value === '') {
            newValue.setErrors({dem_phoneInvalid: true});
            errors['dem_phoneInvalid'] = true;
          }

          if(newValue.value === oldValue.value) {
            newValue.setErrors({dem_phoneNewSameOldInvalid: false});
            errors['dem_phoneNewSameOldInvalid'] = true;
          }

          if(otpCode.enabled && (otpCode.value === undefined || otpCode.value === null || otpCode.value === '')) {
            otpCode.setErrors({dem_otpInvalid: true});
            errors['dem_otpInvalid'] = true;
          }
        } break;
        case 'email': {
          let oldValue = (control as FormGroup).controls['old_value'];
          let newValue = (control as FormGroup).controls['new_value'];
          let otpCode = (control as FormGroup).controls['otp_code'];

          if(newValue.value === undefined || newValue.value === null || newValue.value === '') {
            newValue.setErrors({dem_emailInvalid: true});
            errors['dem_emailInvalid'] = true;
          }

          if(newValue.value && Object.keys(newValue.errors || {}).length > 0) {
            newValue.setErrors({dem_emailPatternInvalid: true});
            errors['dem_emailPatternInvalid'] = true;
          }

          if(newValue.value === oldValue.value) {
            newValue.setErrors({dem_emailNewSameOldInvalid: false});
            errors['dem_emailNewSameOldInvalid'] = true;
          }

          if(otpCode.enabled && (otpCode.value === undefined || otpCode.value === null || otpCode.value === '')) {
            otpCode.setErrors({dem_otpInvalid: true});
            errors['dem_otpInvalid'] = true;
          }
        } break;
        case 'address': {
          let address = (control as FormGroup).controls['address'];
          let addressNumber = (control as FormGroup).controls['addressNumber'];
          let postalCode = (control as FormGroup).controls['postalCode'];
          let city = (control as FormGroup).controls['city'];

          if(address.value === undefined || address.value === null || address.value === '') {
            address.setErrors({dem_addressInvalid: true});
            errors['dem_addressInvalid'] = true;
          }

          if(addressNumber.value === undefined || addressNumber.value === null || addressNumber.value === '') {
            addressNumber.setErrors({dem_addressNumberInvalid: true});
            errors['dem_addressNumberInvalid'] = true;
          }

          if(postalCode.value === undefined || postalCode.value === null || postalCode.value === '') {
            postalCode.setErrors({dem_postalCodeInvalid: true});
            errors['dem_postalCodeInvalid'] = true;
          }

          if(city.value === undefined || city.value === null || city.value === '') {
            city.setErrors({dem_cityInvalid: true});
            errors['dem_cityInvalid'] = true;
          }

        } break;
        default: {

        } break;
      }

      return Object.keys(errors).length > 0 ? errors : null;
    }
  }

  validate = (form: FormGroup): boolean => {
    Object.keys(form.controls).forEach((control) => {
      console.log(control);
      console.log(
        form.controls[control].errors
      );
      form.controls[control].markAsTouched();
    })

    return true;
  }

  protected options = signal<TableOptions<Demographics>>(
    new TableOptions<Demographics>({
      id: 'demographics',
      type: 'Demographics',
      interface: Demographics,
      autoLoadData: false,
      ignoreColumns: ['country'],
      searchComponent: null,
    })
  );

  protected modalOptions = model<ModalOptions>(
    new ModalOptions({
      id: 'modal',
      title: "preview_document",
      type: ModalTypes.form,
      form: FormComponent,
      options: {
        animation: true,
        size: 'md',
        fullscreen: 'sm',
        backdrop: 'static',
        keyboard: false,
      },
      onSave: () => {this.onSave()},
      onClose: () => {this.onFormReset()},
      onReset: () => {this.onFormReset()}
    })
  );

  protected oldEmailInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'oldEmail',
      name: 'oldEmail',
      label: {
        id: 'oldEmail',
        text: 'old_email',
      },
      type: InputTypes.email,
      formControl: this.emailForm.get('old_value') as FormControl,
      disabled: true,
    })
  );

  protected newEmailInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'newEmail',
      name: 'newEmail',
      label: {
        id: 'newEmail',
        text: 'new_email',
      },
      type: InputTypes.email,
      formControl: this.emailForm.get('new_value') as FormControl,
      required: true,
    })
  );

  protected oldPhoneInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'oldPhone',
      name: 'oldPhone',
      label: {
        id: 'oldPhone',
        text: 'old_phone',
      },
      type: InputTypes.tel,
      formControl: this.phoneForm.get('old_value') as FormControl,
      disabled: true,
    })
  );

  protected newPhoneInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'newPhone',
      name: 'newPhone',
      label: {
        id: 'newPhone',
        text: 'new_phone',
      },
      type: InputTypes.tel,
      formControl: this.phoneForm.get('new_value') as FormControl,
      required: true,
    })
  );

  protected emailOTPInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'otp',
      label: {
        text: 'otp',
      },
      type: InputTypes.text,
      mode: InputModes.numeric,
      name: 'otp',
      //pattern: FILTER_OTP_NUMBERS,
      formControl: this.emailForm.get('otp_code') as FormControl,
      initialValue: '',
      //onInput: (input: HTMLInputElement, event?: Event) => {this.onNumericInput(input)},
      required: true
    })
  )

  protected phoneOTPInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'otp',
      label: {
        text: 'otp',
      },
      type: InputTypes.text,
      mode: InputModes.numeric,
      name: 'otp',
      //pattern: FILTER_OTP_NUMBERS,
      formControl: this.phoneForm.get('otp_code') as FormControl,
      initialValue: '',
      //onInput: (input: HTMLInputElement, event?: Event) => {this.onNumericInput(input)},
      required: true
    })
  )

  // Address

  protected addressInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'address',
      label: {
        text: 'address',
      },
      type: InputTypes.text,
      name: 'address',
      formControl: this.address,
      initialValue: '',
      required: true
    })
  )

  protected addressNumberInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'number',
      label: {
        text: 'number',
      },
      type: InputTypes.text,
      name: 'address',
      pattern: /[a-zA-Z0-9]+$/,
      formControl: this.number,
      initialValue: '',
      required: true
    })
  )

  protected addressPostCodeInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'post_code',
      label: {
        text: 'post_code',
      },
      type: InputTypes.text,
      name: 'address',
      formControl: this.postCode,
      initialValue: '',
      required: true
    })
  )

  protected addressCityInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'city',
      label: {
        text: 'city',
      },
      type: InputTypes.text,
      name: 'address',
      formControl: this.city,
      initialValue: '',
      required: true
    })
  )

  protected addressCountryInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'country',
      label: {
        text: 'country',
      },
      type: InputTypes.text,
      name: 'country',
      formControl: this.country,
      initialValue: 'cy',
      disabled: true
    })
  )

  ngOnInit(): void {
    const pData = this.patientService.data
    this.options.set(
      new TableOptions<Demographics>({
        id: 'demographics',
        type: 'Demographics',
        interface: Demographics,
        data: [{
          nationalId: pData?.nationalId,
          phone: pData?.phone,
          email: pData?.email,
          address: pData?.full_address,
          country: pData?.country,
        }],
        autoLoadData: false,
        ignoreColumns: ['country'],
        pagination: undefined,
        searchComponent: null,
        renderCustomRow: (td: HTMLTableCellElement, row: Demographics, item: string) => {
          const data = row as Demographics;

          if(environment.project === PROJECTS.PROVIDERS) {}

          if(environment.project === PROJECTS.BENEFICIARIES) {
            if(['phone', 'email', 'address'].some((el) => el === item)) {
              this.createEntry(td, item, data?.[item])
            } else {
              this.createEntry(td, item, data?.[item], false)
            }
          }
        }
      })
    );
  }

  protected createEntry = (td: HTMLTableCellElement, item: string, data: string, withButton: boolean = true) => {
    td.innerHTML = '';

    let mainDiv = this.renderer2.createElement('div');
    let container = this.renderer2.createElement('div');
    this.renderer2.addClass(mainDiv, 'd-flex');
    //this.renderer2.addClass(mainDiv, 'flex-grow-1');
    this.renderer2.addClass(mainDiv, 'flex-row');

    this.renderer2.addClass(container, 'd-flex');
    this.renderer2.addClass(container, 'align-items-baseline');
    this.renderer2.addClass(container, 'justify-content-between');

    this.renderer2.addClass(container, 'flex-grow-1');

    this.renderer2.appendChild(mainDiv, container);

    let span = this.renderer2.createElement('span');
    let text = this.renderer2.createText(data);
    this.renderer2.appendChild(span, text)

    let button = this.renderer2.createElement('button');
    if(withButton) {

      this.renderer2.addClass(button, 'btn');
      this.renderer2.addClass(button, 'btn-primary');
      this.renderer2.addClass(button, 'btn-sm');
      this.renderer2.addClass(button, 'bi');
      this.renderer2.addClass(button, 'bi-pencil-fill');
      this.renderer2.addClass(button, 'ms-1');

      this.renderer2.listen(button, 'click', (e) => {
        button.blur();
        this.modalOptions().title = 'edit_' + item;

        if(item === 'address') {
          this.modalOptions().options!.size = 'lg';
          this.modalOptions().options!.fullscreen = 'md';
        } else {
          this.modalOptions().options!.size = 'md';
          this.modalOptions().options!.fullscreen = 'sm';
        }

        this.mod.open();

        this.mod.detectChanges();
      })
    }

    this.renderer2.appendChild(container, span);

    if(withButton) {
      this.renderer2.appendChild(container, button);
    }

    this.renderer2.setStyle(td, 'vertical-align', 'baseline');

    this.renderer2.appendChild(td, mainDiv);
  }

  onSave = () => {
    if(this.modalOptions().title === 'edit_email') {
      this.emailFormEl?.ngSubmit.emit();
    }
    if(this.modalOptions().title === 'edit_phone') {
      this.phoneFormEl?.ngSubmit.emit();
    }
    if(this.modalOptions().title === 'edit_address') {
      this.addressFormEl?.ngSubmit.emit();
    }
  }

  onFormReset = () => {
    console.log('on reset form');
    if(this.modalOptions().title === 'edit_email') {
      this.emailForm.reset({
        old_value: this.patientService.data?.email,
      });
      this.newEmail?.reset();
      this.newEmailAddress?.enable();
      this.emailOTPCode.clearValidators();
      this.emailOTPCode.disable();
    } else if (this.modalOptions().title === 'edit_phone') {
      //this.phoneForm.controls['old_value'].setValue(this.patientService.data?.phone);
      this.phoneForm.reset({
        old_value: this.patientService.data?.phone,
      });
      this.newPhone?.reset();
      this.newPhoneNumber?.enable();
      this.phoneOTPCode.clearValidators();
      this.phoneOTPCode.disable();
    } else if (this.modalOptions().title === 'edit_address') {
      this.addressForm.reset({
        address: this.patientService.data?.address?.address,
        addressNumber: this.patientService.data?.address?.addressNumber,
        postalCode: this.patientService.data?.address?.postalCode,
        city: this.patientService.data?.address?.city,
        country: this.patientService.data?.address?.country,
      });
    }

    this.modalOptions().errors = [];
  }


  protected onNumericInput = (input: HTMLInputElement): any => {
    return input.value.replace(FILTER_ONLY_NUMBERS, '');
  };

  onFormSubmit = (form: FormGroup, type: string) => {
    if (this.validate(form) && form.invalid) {
      console.log('form.is.invalid');
      this.modalOptions().errors = form.errors!;
      this.mod.detectChanges();
      return;
    }

    this.modalOptions().disableOnSave = true;
    if (type === 'email' || type === 'phone') {
      if (form.controls['new_value'].enabled) {
        let body = {
          verification_type: type === 'email' ? 'EMAIL' : type === 'phone' ? 'PHONE' : '',
          old_value: form.controls['old_value'].value,
          new_value: form.controls['new_value'].value,
        }

        form.controls['new_value'].disable();

        this.apiService.call({
          path: '/request-update-contact-details',
          headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
          type: HTTPMethods.POST,
          body: body
        })?.subscribe(res => {
          if (res && res.status?.toString() === '1') {
            console.log(res);
            if (type === 'email') {
              this.emailOTPCode.enable();
              this.emailOTPCode.setValidators([Validators.required]);
            } else if (type === 'phone') {
              this.phoneOTPCode.enable();
              this.phoneOTPCode.setValidators([Validators.required]);
            }
            this.modalOptions().disableOnSave = false;
          }
        });
      } else if (form.controls['new_value'].disabled && form.controls['otp_code'].enabled) {
        let body = {
          verification_type: type === 'email' ? 'EMAIL' : type === 'phone' ? 'PHONE' : '',
          new_value: form.controls['new_value'].value,
          otp_code: form.controls['otp_code'].value,
        }

        this.apiService.call({
          path: '/confirm-update-contact-details',
          headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
          type: HTTPMethods.POST,
          body: body
        })?.subscribe(res => {
          if (res && res.status?.toString() === '1') {
            let data = this.demographicsEl.options().data;
            let localPatient = this.patientService.patient;

            if (type === 'email') {
              data![0].email = this.newEmailAddress.value;
              localPatient!.data!.email = this.newEmailAddress.value;
            } else if (type === 'phone') {
              data![0].phone = this.newPhoneNumber.value;
              localPatient!.data!.phone = this.newPhoneNumber.value;
            }

            this.patientService.patient = localPatient;
            //this.demographicsEl.tableData = data || [];
            this.modalOptions().disableOnSave = false;
            this.mod.close();
          }
        });
      }
    } else if (type === 'address') {
      let body = {
        address: form.controls['address'].value,
        addressNumber: form.controls['addressNumber'].value,
        postalCode: form.controls['postalCode'].value,
        city: form.controls['city'].value,
        country: form.controls['country'].value,
      }

      this.apiService.call({
        path: '/request-update-address',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        type: HTTPMethods.POST,
        body: body
      })?.subscribe(res => {
        if (res && res.status?.toString() === '1') {
          let data = this.demographicsEl.options().data;
          console.log(data);
          let localPatient = this.patientService.patient;

          // TODO: change data from res data instead
          data![0].address = body.address + ' ' + body.addressNumber + ', ' +body.city + ', ' + body.postalCode + ', ' + body.country;
          localPatient!.data!.address = body;

          this.patientService.patient = localPatient;
          //this.demographicsEl.tableData = data || [];
          this.modalOptions().disableOnSave = false;
          this.mod.close();
        }
      });
    }
  }

  protected readonly Validators = Validators;
}
